<script setup lang="tsx">
import { ref, watch, computed } from 'vue'
import type { ExportResult, JiraResponse, Response } from '@/models/api-generated'
import {
	type Status,
	ImportInProgress,
	ExportCompleted,
	ExportInProgress,
	TaskUnsuccessful,
	ConfigurationMissing,
	NoDomainConfigForInstance,
	DeleteInProgress,
	DeleteCompleted,
	ConfirmDelete,
	StartDelete,
	StartExport,
} from '@/models/jira-enums-generated'
import jiraCbStore from '@/stores/jira-checkboxes'
import { getTranslatedJiraResponseText } from '@/services/utils'
import { Modal } from 'bootstrap'
import { storeToRefs } from 'pinia'
import methodEditor from '@/stores/method-editor'
import http from '@/services/http'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const jiraData = ref<JiraResponse | null>(null)
const jiraCb = jiraCbStore()
const { isShowModal, jiraStatus, action } = storeToRefs(jiraCb)
const editor = methodEditor()
let modalInstance: Modal | null = null

const performApiCall = async (endpoint: string, completedStatus: Status): Promise<void> => {
	const idsArray = editor.massEdit.selected.map((metadata) => metadata.id)
	const request = { smmdIDs: idsArray }
	const { data } = await http.post<Response<JiraResponse>>(endpoint, request)
	if (data.data) jiraData.value = data.data
	if (!jiraData.value) {
		jiraStatus.value = TaskUnsuccessful
		return
	}
	editor.massEdit.selected = []
	await editor.refresh()
	jiraStatus.value = completedStatus
}

const updateStatusAfterApiCall = async (action: () => Promise<void>, loadingStatus: Status) => {
	jiraStatus.value = loadingStatus
	await action()
}

async function startExport() {
	await updateStatusAfterApiCall(() => performApiCall(`/api/jira/startExport`, ExportCompleted), ExportInProgress)
}

async function deleteIssues() {
	await updateStatusAfterApiCall(() => performApiCall(`/api/jira/deleteIssues`, DeleteCompleted), DeleteInProgress)
}

const closeModal = () => {
	jiraData.value = null
	isShowModal.value = false
	if (modalInstance) {
		modalInstance.hide()
	}
}

watch(isShowModal, async (newValue) => {
	if (newValue) {
		const modalElement = document.getElementById('modal')!
		modalInstance = new Modal(modalElement, {
			backdrop: 'static',
			keyboard: false,
		})
		modalInstance.show()
		if (!isLoading.value) {
			if (action.value === StartExport) await startExport()
			if (action.value === StartDelete) jiraStatus.value = ConfirmDelete
		}
		modalElement.addEventListener('hidden.bs.modal', () => {
			isShowModal.value = false
		})
	}
})
const redirectToSettings = () => {
	window.location.href = '/app/settings'
}

function isStatus(status?: Status, ...statuses: Status[]): boolean {
	if (!status) {
		return false
	}
	return statuses.includes(status)
}
const isLoading = computed(() => isStatus(jiraStatus.value, ExportInProgress, ImportInProgress, DeleteInProgress))

const showSettingsRoutingBtn = computed(() => {
	return isStatus(jiraStatus.value, ConfigurationMissing, NoDomainConfigForInstance) || jiraData.value?.status === NoDomainConfigForInstance
})

const computeCounts = () => {
	const counts = {
		methodSuccessfulCount: 0,
		methodUpdatedSuccessfulCount: 0,
		methodFailedCount: 0,
		methodUpdatedFailedCount: 0,
		allExportedCount: 0,
	}
	if (!jiraData.value?.methodResults) {
		return counts
	}

	Object.values(jiraData.value.methodResults).forEach((result) => {
		counts.allExportedCount++
		if (!result.failure && !result.updatedResult) {
			counts.methodSuccessfulCount++
		} else if (!result.failure && result.updatedResult) {
			counts.methodUpdatedSuccessfulCount++
		} else if (result.failure && !result.updatedResult) {
			counts.methodFailedCount++
		} else if (result.failure && result.updatedResult) {
			counts.methodUpdatedFailedCount++
		}
	})
	return counts
}

const counts = computed(computeCounts)

const getFailedResults = (results: ExportResult[]) => results.filter((result) => result.failure)

const groupFailuresByInstance = (failedMethods: ExportResult[], failedModules: ExportResult[]) => {
	const groupedFailures: Record<string, any[]> = {}

	failedMethods.forEach((result) => {
		const instance = result.instance
		if (!groupedFailures[instance]) groupedFailures[instance] = []

		groupedFailures[instance].push({
			methodName: result.methodName,
			failureReason: result.failureReasons || '',
			ruleFailures:
				result.ruleResults
					?.filter((r) => r.failure)
					.map((r) => ({
						ruleName: t(r.methodName.slice(0, 4)) + r.methodName.slice(4),
						failureReason: r.failureReasons || '',
					})) || [],
		})
	})

	failedModules.forEach((result) => {
		const instance = result.instance
		if (!groupedFailures[instance]) groupedFailures[instance] = []

		groupedFailures[instance].push({
			moduleName: result.methodName,
			failureReason: result.failureReasons || '',
		})
	})

	return groupedFailures
}

const computeInstanceFailures = () => {
	if (!jiraData.value) return []

	const failedMethods = getFailedResults(Object.values(jiraData.value.methodResults))
	const failedModules = getFailedResults(Object.values(jiraData.value.moduleResults))
	const groupedFailures = groupFailuresByInstance(failedMethods, failedModules)

	return Object.entries(groupedFailures).map(([instance, failures]) => ({ instance, failures }))
}

function translateError(errorMessage: string) {
	const fieldErrorPattern = /Field '(.+?)' cannot be set\. It is not on the appropriate screen, or unknown\./
	const match = errorMessage.match(fieldErrorPattern)

	if (match) {
		const originalFieldName = match[1]
		const translatedFieldName = t(`jira.fieldNames.${originalFieldName}`, originalFieldName)
		return t('apiErrors.fieldCannotBeSet', { fieldName: translatedFieldName })
	}
	const cleanKey = errorMessage.replace(/[:.]/g, '').replace(/\s+/g, ' ').trim()
	return t(`apiErrors.${cleanKey}`, errorMessage)
}

const instanceFailures = computed(computeInstanceFailures)

const getJiraModalMessage = computed(() => getTranslatedJiraResponseText(jiraStatus.value))
</script>

<template>
	<div class="modal fade" id="modal" tabindex="-1" aria-hidden="true">
		<div class="modal-dialog">
			<div class="modal-content">
				<div class="modal-header">
					<h3 class="modal-title">{{ $t('jira.label') }}</h3>
					<button type="button" class="btn-close" aria-label="Close" @click="closeModal"></button>
				</div>
				<div class="modal-body">
					<span>{{ getJiraModalMessage }}</span>
					<div v-if="isLoading" class="d-flex justify-content-center align-items-center mt-3">
						<div class="spinner-border" role="status">
							<span class="visually-hidden">{{ $t('misc.loading') }}</span>
						</div>
					</div>

					<div v-if="!isLoading && jiraData?.methodResults" class="mt-4">
						<h6>{{ $t('misc.result') }}:</h6>
						<div v-if="counts.methodSuccessfulCount">
							{{ $t('jira.methodSuccessfulCount') }}: {{ counts.methodSuccessfulCount }}/{{ counts.allExportedCount }}
						</div>
						<div v-if="counts.methodUpdatedSuccessfulCount">
							{{ $t('jira.methodUpdateSuccessfulCount') }}: {{ counts.methodUpdatedSuccessfulCount }}/{{ counts.allExportedCount }}
						</div>
						<div v-if="counts.methodFailedCount">
							{{ $t('jira.methodFailedCount') }}: {{ counts.methodFailedCount }}/{{ counts.allExportedCount }}
						</div>
						<div v-if="counts.methodUpdatedFailedCount">
							{{ $t('jira.methodUpdatedFailedCount') }}: {{ counts.methodUpdatedFailedCount }}/{{ counts.allExportedCount }}
						</div>
						<hr v-if="instanceFailures.length > 0" class="my-4 border-top border-secondary" />
						<div v-if="instanceFailures.length > 0">
							<div>{{ $t('jira.failedMethods') }}:</div>
							<ul v-for="(failure, index) in instanceFailures" :key="index">
								<div>
									<strong>{{ failure.instance }}</strong>
								</div>
								<ul>
									<li v-for="(item, itemIndex) in failure.failures" :key="itemIndex">
										<p>
											{{ item.methodName || item.moduleName }}:
											{{ translateError(item.failureReason) }}
										</p>
										<ul v-if="item.ruleFailures">
											<li v-for="(rule, ruleIndex) in item.ruleFailures" :key="ruleIndex">
												<p>{{ rule.ruleName }}: {{ translateError(rule.failureReason) }}</p>
											</li>
										</ul>
									</li>
								</ul>
							</ul>
						</div>
					</div>
				</div>
				<div class="modal-footer">
					<div v-if="jiraStatus === ConfirmDelete">
						<button type="button" class="btn btn-secondary me-3" @click="closeModal">{{ $t('misc.cancel') }}</button>
						<button type="button" class="btn btn-danger" @click="deleteIssues()">{{ $t('misc.confirm') }}</button>
					</div>
					<div v-else-if="!isLoading">
						<button type="button" class="btn btn-secondary" @click="closeModal">{{ $t('misc.continue') }}</button>
					</div>
					<button v-if="showSettingsRoutingBtn" type="button" class="btn btn-primary" @click="redirectToSettings">
						{{ $t('jira.goToSettings') }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="sass" scoped>
.loading-container
	display: flex
	justify-content: center
	align-items: center
	margin: 20px 0
</style>
